/**
 * SponsorChallengesRow — sponsor engagement widgets beneath the player.
 *
 * Renders the three engagement mechanics as equal, self-contained columns so
 * each is a real, visible widget rather than an "Open Slot" chrome tile:
 *   LIVE:    3 columns — Sponsor Challenge (journey) · Leaderboard · Bingo
 *   OFFLINE: 2 columns — Leaderboard · Bingo
 *            (the journey renders in StreamPanel's right rail via
 *             ActiveChallengePanel when the creator is offline, so it's
 *             omitted here to avoid duplication.)
 *
 * Each column wraps a mechanic (MechanicJourney / MechanicLeaderboard /
 * MechanicBingo) in a MechanicSlot with "Open Slot" chrome. Placeholder data
 * drives the widgets so the surfaces are demoable before a real sponsor
 * activation wires in.
 */

import React from "react";
import { MechanicSlot, type MechanicBrandChrome } from "./mechanics/MechanicSlot";
import { MechanicJourney, type JourneyStep } from "./mechanics/MechanicJourney";
import { MechanicLeaderboard, type LeaderboardRow } from "./mechanics/MechanicLeaderboard";
import { MechanicBingo, type BingoCell } from "./mechanics/MechanicBingo";

interface Props {
    hubName?: string;
    isLive?: boolean;
}

const JOURNEY_STEPS: JourneyStep[] = [
    { id: "1", label: "Visit the sponsor", done: false, active: false },
    { id: "2", label: "Discover the offer", done: false, active: false },
    { id: "3", label: "In-game objective", done: false, active: false },
    { id: "4", label: "Complete all to claim", done: false, active: false },
];

const LB_ROWS: LeaderboardRow[] = [
    { id: "sample-1", rank: 1, name: "Player One", score: 1240 },
    { id: "sample-2", rank: 2, name: "Player Two", score: 980 },
    { id: "sample-3", rank: 3, name: "Player Three", score: 610 },
];
const LB_YOUR: LeaderboardRow = { id: "sample-you", rank: 7, name: "You", score: 120 };

const BINGO_LABELS = [
    "Ace", "Clutch", "Revive", "Headshot", "Win",
    "Combo", "Assist", "Steal", "Flank", "Snipe",
    "No-scope", "Wallbang", "FREE", "Save", "Bomb",
    "Rush", "Hold", "Trade", "Peek", "Dink",
    "Frag", "Clip", "Emote", "GG", "Streak",
];
const BINGO_CELLS: BingoCell[] = BINGO_LABELS.map((label, i) => ({
    id: String(i),
    label,
    state: i === 12 ? "free" : [0, 6, 18, 24].includes(i) ? "done" : [2, 10].includes(i) ? "line" : "empty",
}));

const openSlot = (title: string): MechanicBrandChrome => ({
    brandKey: "generic",
    brandName: "Open Slot",
    title,
    status: "Slot open",
    ctaLabel: "Sponsor This",
    ctaHref: "#",
});

export const SponsorChallengesRow: React.FC<Props> = ({ hubName: _hubName, isLive }) => {
    /*
     * Dev-only gate. These are "Open Slot" placeholder widgets — no real
     * sponsor activation data yet — so they render on dev / staging / Mirror5
     * for demos and stay off in production, where empty slots read as broken.
     * Matches the CreatorDeliveryRibbon env gate.
     */
    if (import.meta.env.VITE_ENVIRONMENT === "production") return null;

    const cols = isLive ? 3 : 2;

    return (
        <div
            className="hcs-sponsors-grid"
            style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: 16 }}
        >
            {isLive &&
                <MechanicSlot variant="marquee" {...openSlot("Sponsor Challenge")}>
                    <MechanicJourney variant="marquee" steps={JOURNEY_STEPS} />
                </MechanicSlot>
            }
            <MechanicSlot variant="marquee" {...openSlot("Leaderboard")}>
                <MechanicLeaderboard variant="marquee" rows={LB_ROWS} yourRow={LB_YOUR} note="Sample board — activate to go live" />
            </MechanicSlot>
            <MechanicSlot variant="marquee" {...openSlot("Bingo")}>
                <MechanicBingo variant="marquee" cells={BINGO_CELLS} gridSize={5} />
            </MechanicSlot>
        </div>
    );
};
